import { pageConst } from "@src/constants/pageConst";
import { SessionResponseData } from "@src/types/api/v1/sessions";
import { sanitizeErrorPageDisplayMessage } from "@src/utils/assertSessionCode";
import { NextRouter } from "next/router";
// エラーコードの定義
export const ERROR_CODES = {
  // セッション関連エラー
  SESSION_INVALID: "session_invalid", // セッションが無効
  SESSION_EXPIRED: "session_expired", // セッションの有効期限切れ
  SESSION_NOT_FOUND: "session_not_found", // セッションが見つからない
  SESSION_ERROR: "session_error", // その他のセッションエラー

  // API関連エラー
  BAD_REQUEST: "bad_request", // リクエスト形式が不正
  UNAUTHORIZED: "unauthorized", // 認証エラー
  FORBIDDEN: "forbidden", // 権限なし
  NOT_FOUND: "not_found", // リソースが見つからない
  SERVER_ERROR: "server_error", // サーバー内部エラー
  SERVICE_UNAVAILABLE: "service_unavailable", // サービス利用不可

  // その他
  UNKNOWN_ERROR: "unknown_error", // 未知のエラー
};

// セッション関連のストレージキー
export const SESSION_STORAGE_KEYS = {
  SESSION_ID: "session_id",
  EXPIRE_AT: "session_expire_at",
};

/**
 * APIとの連携を含むセッション検証を行う統合関数
 * @param sessionId セッションID
 * @param getSessionInfo セッション情報取得関数
 * @param router Next.jsルーター
 * @returns 検証結果とセッション情報のオブジェクト
 */
export type SessionValidationResult = {
  isValid: boolean;
  sessionInfo?: SessionResponseData | null;
  reason?: string;
};

export const validateSession = async (
  sessionId: string | null,
  getSessionInfo: () => Promise<any>,
  router: NextRouter,
  redirectFlg: boolean = true
): Promise<SessionValidationResult> => {
  try {
    // セッションIDがない場合はエラーページにリダイレクト
    if (!sessionId) {
      if (redirectFlg) {
        redirectToErrorPage(router, ERROR_CODES.SESSION_INVALID);
      }
      return { isValid: false, reason: ERROR_CODES.SESSION_INVALID };
    }

    // セッション情報を取得
    const response = await getSessionInfo();

    // レスポンスがnullまたはundefinedの場合
    if (!response) {
      if (redirectFlg) {
        redirectToErrorPage(router, ERROR_CODES.SESSION_INVALID);
      }
      return { isValid: false, reason: ERROR_CODES.SESSION_INVALID };
    }

    // レスポンスにstatusが含まれている場合（APIクライアントの戻り値の形式によって異なる）
    if (response.status && response.status !== 200) {
      console.error(
        `セッション取得エラー: ステータスコード ${response.status}`
      );

      // ステータスコードに応じたエラーコードを設定
      const errorCode = getErrorCodeFromStatus(response.status);
      if (redirectFlg) {
        redirectToErrorPage(router, errorCode, undefined, response.status);
      }
      return { isValid: false, reason: errorCode };
    }

    // dataプロパティにデータが含まれている場合（APIクライアントの戻り値の形式によって異なる）
    const sessionInfo = response.data || response;

    // セッション情報が取得できない場合はエラーページに遷移
    if (!sessionInfo) {
      if (redirectFlg) {
        redirectToErrorPage(router, ERROR_CODES.SESSION_INVALID);
      }
      return { isValid: false, reason: ERROR_CODES.SESSION_INVALID };
    }

    // 有効期限切れの場合はエラーページに遷移
    if (sessionInfo.expire_at) {
      let expireTime: number;

      // 数値またはISO文字列形式の両方に対応
      if (typeof sessionInfo.expire_at === "number") {
        expireTime = sessionInfo.expire_at;
      } else {
        // ISO文字列の場合はDateオブジェクトに変換してからタイムスタンプに
        const expireDate = new Date(sessionInfo.expire_at);
        expireTime = Math.floor(expireDate.getTime() / 1000); // 秒単位のUNIXタイムスタンプに変換
      }

      // 現在時刻を秒単位のUNIXタイムスタンプに変換
      const now = Math.floor(Date.now() / 1000);
      // console.log("現在時刻(Unix):", now);
      // console.log("有効期限(Unix):", expireTime);

      // 現在時刻が有効期限を超えている場合（有効期限切れ）
      if (now > expireTime) {
        if (redirectFlg) {
          redirectToErrorPage(
            router,
            ERROR_CODES.SESSION_EXPIRED,
            undefined,
            getStatusFromErrorCode(ERROR_CODES.SESSION_EXPIRED) ?? undefined
          );
        }
        return { isValid: false, reason: ERROR_CODES.SESSION_EXPIRED };
      }

      // 有効期限内の場合はセッションストレージに保存
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.EXPIRE_AT,
        expireTime.toString()
      );
    }

    return { isValid: true, sessionInfo };
  } catch (error) {
    console.error("セッション検証エラー:", error);

    // エラーオブジェクトにレスポンスが含まれている場合（Axios等のAPIクライアント）
    if (error.response && error.response.status) {
      const status = error.response.status;
      // ステータスコードに応じたエラーコードを設定
      const errorCode = getErrorCodeFromStatus(status);
      if (redirectFlg) {
        redirectToErrorPage(router, errorCode, undefined, status);
      }
      return { isValid: false, reason: errorCode };
    }

    // その他のエラーの場合
    if (redirectFlg) {
      redirectToErrorPage(
        router,
        ERROR_CODES.SESSION_ERROR,
        undefined,
        getStatusFromErrorCode(ERROR_CODES.SESSION_ERROR) ?? undefined
      );
    }

    return { isValid: false, reason: ERROR_CODES.SESSION_ERROR };
  }
};

/**
 * HTTPステータスコードからエラーコードを取得する
 * @param status HTTPステータスコード
 * @returns エラーコード
 */
export const getErrorCodeFromStatus = (status: number): string => {
  switch (status) {
    case 400:
      return ERROR_CODES.BAD_REQUEST;
    case 401:
      return ERROR_CODES.UNAUTHORIZED;
    case 403:
      return ERROR_CODES.FORBIDDEN;
    case 404:
      return ERROR_CODES.NOT_FOUND;
    case 500:
      return ERROR_CODES.SERVER_ERROR;
    case 503:
      return ERROR_CODES.SERVICE_UNAVAILABLE;
    default:
      return status >= 500
        ? ERROR_CODES.SERVER_ERROR
        : ERROR_CODES.UNKNOWN_ERROR;
  }
};

/**
 * エラーコードから代表的な HTTP ステータスコードを推定する
 * （/error ページでの表示用。厳密な逆変換ではなく、画面表示のための目安。）
 */
export const getStatusFromErrorCode = (
  errorCode: string | undefined | null
): number | null => {
  if (!errorCode) return null;
  switch (errorCode) {
    // セッション関連
    case ERROR_CODES.SESSION_INVALID:
    case ERROR_CODES.SESSION_EXPIRED:
    case ERROR_CODES.SESSION_NOT_FOUND:
    case ERROR_CODES.SESSION_ERROR:
      // クライアント側で「期限切れ/不正」を検知したケースは 400 相当として扱う
      return 400;

    // API関連
    case ERROR_CODES.BAD_REQUEST:
      return 400;
    case ERROR_CODES.UNAUTHORIZED:
      return 401;
    case ERROR_CODES.FORBIDDEN:
      return 403;
    case ERROR_CODES.NOT_FOUND:
      return 404;
    case ERROR_CODES.SERVER_ERROR:
      return 500;
    case ERROR_CODES.SERVICE_UNAVAILABLE:
      return 503;

    // その他
    case ERROR_CODES.UNKNOWN_ERROR:
      return 500;

    default:
      return null;
  }
};

/**
 * エラーページにリダイレクトする
 * @param router Next.jsルーター
 * @param errorCode エラーコード（オプション）
 * @param errorMessage エラーメッセージ（オプション）
 */
export const redirectToErrorPage = (
  router: NextRouter,
  errorCode?: string,
  errorMessage?: string,
  statusCode?: number
): void => {
  // エラーコードとメッセージをクエリパラメータとして渡す
  const query: Record<string, string> = {};

  if (errorCode) {
    query.code = errorCode;
  }

  if (errorMessage) {
    query.message = sanitizeErrorPageDisplayMessage(errorMessage);
  }

  const normalizedStatus =
    typeof statusCode === "number" && Number.isFinite(statusCode)
      ? statusCode
      : getStatusFromErrorCode(errorCode ?? null);

  if (normalizedStatus !== null) {
    query.status = String(normalizedStatus);
  }
  // エラーページにリダイレクト
  router.push({
    pathname: pageConst.error.href,
    query,
  });
};
